import cx from 'classnames'
import { observer } from 'mobx-react-lite'
import React, { useContext, useEffect, useRef } from 'react'
import { useValidatedState } from 'beautiful-react-hooks'
import { computed } from 'mobx'
import { AlmanacEditorStateContext } from 'stores/EditorState'
import { SingleMenuDropdownContext } from '../SingleMenuDropdown/SingleMenuDropdownStore'
import { fontSizeValidator, useSetFontSize } from '../../components/Format/components/utils/changeFontSize'
import { Button } from '../Buttons/Button'
import { FontSizeContext, FontSizeStore } from './stores/FontSizeStore'
import Styles from './SelectFontSize.module.scss'
import { useForwardRef } from 'hooks/useForwardRef'
import { SimpleDropdown } from '../SimpleDropdown/SimpleDropdown'
import { SimpleDropdownItem } from '../SimpleDropdown/SimpleDropdownItem'
import { ErrorBoundary } from '@sentry/react'
import { IconName, ToolbarIcon } from 'common/Prose/Toolbar/ToolbarIcon'

interface Props {
  className?: string
  inToolbar?: boolean
}

const SelectFontSize = observer(({ className, inToolbar = false }: Props) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const almanacEditorStateContext = useContext(AlmanacEditorStateContext)
  const activeFontSizes = almanacEditorStateContext?.activeFontSizes
  const handleClick = () => {
    inputRef.current?.focus()
    inputRef.current?.setSelectionRange(0, inputRef.current?.value.length)
  }

  return (
    <FontSizeContext.Provider value={new FontSizeStore()}>
      <SimpleDropdown
        buttonClassName={cx(Styles.fontSizeButton, className)}
        aria-label="Change font size"
        clickAction={handleClick}
        placement={inToolbar ? 'right-start' : 'bottom-start'}
        hasChevron={!inToolbar}
        ignoreHover={!inToolbar}
        isSubmenu={inToolbar}
        disabled={!activeFontSizes?.length}
      >
        {inToolbar ? (
          <SimpleDropdownItem
            icon={<ToolbarIcon name={IconName.FontSize} />}
            chevronLabel={activeFontSizes?.[0]?.toString()}
            label="Size"
          />
        ) : (
          <FontInput ref={inputRef} />
        )}
        <FontOptions />
      </SimpleDropdown>
    </FontSizeContext.Provider>
  )
})

const FontInput = observer(
  React.forwardRef<HTMLInputElement>((_props, forwardedInputRef) => {
    const inputRef = useForwardRef<HTMLInputElement>(forwardedInputRef)
    const dropdownStore = useContext(SingleMenuDropdownContext)
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const { activeFontSizes } = useContext(AlmanacEditorStateContext)!
    const fontSizeContext = useContext(FontSizeContext)
    const setFontSize = useSetFontSize()

    const firstFont = activeFontSizes[0] || 16
    const [fontSizeInput, setFontSizeInput, fontSizeValid] = useValidatedState(fontSizeValidator, firstFont)

    useEffect(() => fontSizeContext.includeFontSizes(activeFontSizes.map(Number)), [fontSizeContext, activeFontSizes])

    useEffect(() => {
      if (!inputRef.current) return
      inputRef.current.value = firstFont + ''
    }, [firstFont, inputRef])

    const handleFontSizeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      let fontSize: string | number = parseInt(e.target.value, 10)
      fontSizeContext.setFontSizeInput(fontSize)
      if (fontSize === 0 || isNaN(fontSize)) fontSize = ''
      setFontSizeInput(fontSize)
    }

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (!inputRef.current) return
      if (['Enter', ' '].includes(e.key) && fontSizeValid && fontSizeContext.currentFont) {
        e.preventDefault()
        dropdownStore?.softClose()
        setFontSize(fontSizeContext.currentFont, true)
      }
      if (e.key === 'ArrowDown') {
        fontSizeContext.nextFont()
      }
      if (e.key === 'ArrowUp') {
        fontSizeContext.previousFont()
      }
    }

    return (
      <input
        className={Styles.searchInput}
        ref={inputRef}
        value={fontSizeValid ? fontSizeInput ?? '' : ''}
        onChange={handleFontSizeChange}
        onKeyDown={handleKeyDown}
        maxLength={2}
      />
    )
  }),
)

const FontOptions = observer(() => {
  const fontSizeContext = useContext(FontSizeContext)

  return (
    <>
      {fontSizeContext.fontSizes.map((possibleFontSize) => (
        <FontOption key={possibleFontSize} fontSize={possibleFontSize} />
      ))}
    </>
  )
})

const FontOption = observer(({ fontSize }: { fontSize: number }) => {
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const { activeFontSizes } = useContext(AlmanacEditorStateContext)!
  const setFontSize = useSetFontSize()
  const dropdownStore = useContext(SingleMenuDropdownContext)
  const fontSizeContext = useContext(FontSizeContext)

  const active = computed(() => activeFontSizes.includes(fontSize)).get()
  const currentFont = activeFontSizes[0] || fontSizeContext.currentFont
  const selected = computed(() => currentFont === fontSize).get()

  return (
    <Button
      aria-label={`Change font size to ${fontSize}pt`}
      active={active}
      selected={selected}
      tabIndex={0}
      className={cx(Styles.fontSize, { [Styles.selected]: selected })}
      onClick={() => {
        setFontSize(fontSize, true)
        dropdownStore?.softClose()
      }}
    >
      {fontSize}
    </Button>
  )
})

const WithErrorBoundary = (props: Props) => (
  <ErrorBoundary>
    <SelectFontSize {...props} />
  </ErrorBoundary>
)

export { WithErrorBoundary as SelectFontSize }
